import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Sparkles, User, LogOut, Menu, X, Home, Compass, Bookmark, Heart, Youtube, ShieldCheck, Users, ChevronDown } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../lib/AuthContext";
import { startGoogleLogin } from "../lib/googleAuth";
import GoogleSignInButton from "./GoogleSignInButton";
import Logo from "./Logo";
import NotificationBell from "./NotificationBell";
import SearchAutocomplete from "./SearchAutocomplete";
import GoldBadge from "./GoldBadge";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Navbar() {
  const navigate = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");
  const { user, logout } = useAuth();
  const [adult, setAdult] = useState(() => localStorage.getItem("fm_adult") === "1");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("fm_adult", adult ? "1" : "0");
  }, [adult]);

  // Close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (mobileOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const onSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const login = () => { startGoogleLogin(); };

  const isAdmin = user && ["owner", "admin", "editor"].includes((user.role || "").toLowerCase());
  const [browseOpen, setBrowseOpen] = useState(false);
  const browseRef = useRef(null);

  // Close Browse dropdown on outside click
  useEffect(() => {
    if (!browseOpen) return;
    const handler = (e) => { if (browseRef.current && !browseRef.current.contains(e.target)) setBrowseOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [browseOpen]);

  const TOP_GENRES    = ["Action","Drama","Thriller","Comedy","Romance","Horror","Science Fiction","Crime","Animation"];
  const TOP_LANGS     = [{n:"Hindi",s:"hindi"},{n:"English",s:"english"},{n:"Tamil",s:"tamil"},{n:"Telugu",s:"telugu"},{n:"Malayalam",s:"malayalam"},{n:"Korean",s:"korean"},{n:"Kannada",s:"kannada"},{n:"Bengali",s:"bengali"}];
  const TOP_YEARS     = [2026, 2025, 2024, 2023, 2022];

  const NavLink = ({ to, children, testid }) => (
    <Link
      to={to}
      data-testid={testid}
      className={`text-sm font-medium tracking-wide transition-colors ${
        loc.pathname === to ? "text-white" : "text-neutral-400 hover:text-white"
      }`}
    >
      {children}
    </Link>
  );

  return (
    <header className="fixed top-0 left-0 right-0 z-50 fm-glass" data-testid="navbar">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center gap-3 sm:gap-6">
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <Logo size={30} testid="brand-logo" />
        </Link>

        <nav className="hidden md:flex items-center gap-6">
          <NavLink to="/" testid="nav-home">Home</NavLink>
          <NavLink to="/discover" testid="nav-discover">Discover</NavLink>
          {/* Browse dropdown */}
          <div className="relative" ref={browseRef}>
            <button
              onClick={() => setBrowseOpen(o => !o)}
              data-testid="nav-browse-btn"
              className={`flex items-center gap-1 text-sm font-medium tracking-wide transition-colors ${browseOpen ? "text-white" : "text-neutral-400 hover:text-white"}`}
            >
              Browse <ChevronDown className={`w-3.5 h-3.5 transition-transform ${browseOpen ? "rotate-180" : ""}`} />
            </button>
            {browseOpen && (
              <div className="absolute left-0 top-full mt-2 w-[540px] rounded-xl border border-white/10 bg-[#111] shadow-2xl p-5 z-50 grid grid-cols-3 gap-5">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-2.5">Genres</p>
                  <ul className="space-y-1.5">
                    {TOP_GENRES.map(g => {
                      const s = g.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                      return (
                        <li key={s}>
                          <Link to={`/genre/${s}`} onClick={() => setBrowseOpen(false)}
                            className="text-sm text-neutral-300 hover:text-white transition-colors"
                            data-testid={`nav-genre-${s}`}>{g}</Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-2.5">Language</p>
                  <ul className="space-y-1.5">
                    {TOP_LANGS.map(l => (
                      <li key={l.s}>
                        <Link to={`/language/${l.s}`} onClick={() => setBrowseOpen(false)}
                          className="text-sm text-neutral-300 hover:text-white transition-colors"
                          data-testid={`nav-lang-${l.s}`}>{l.n}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-2.5">By Year</p>
                  <ul className="space-y-1.5">
                    {TOP_YEARS.map(yr => (
                      <li key={yr}>
                        <Link to={`/year/${yr}`} onClick={() => setBrowseOpen(false)}
                          className="text-sm text-neutral-300 hover:text-white transition-colors"
                          data-testid={`nav-year-${yr}`}>{yr}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
          <NavLink to="/creators" testid="nav-creators">Creators</NavLink>
          <NavLink to="/concierge" testid="nav-concierge">
            <span className="flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-[#007AFF]" />
              Ask Filmi
            </span>
          </NavLink>
          <NavLink to="/watchlist" testid="nav-watchlist">Watchlist</NavLink>
          <NavLink to="/social" testid="nav-social">
            <span className="flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-[#E50914]" />
              Social
            </span>
          </NavLink>
        </nav>

        <form onSubmit={onSearch} className="ml-auto flex-1 max-w-xs hidden sm:block" data-testid="nav-search-form-legacy" style={{display: "none"}} />
        <div className="ml-auto flex-1 max-w-xs hidden sm:block">
          <SearchAutocomplete />
        </div>

        <label className="hidden md:flex items-center gap-2 cursor-pointer select-none" data-testid="adult-toggle-wrap" title={adult ? "Mature content allowed (R-rated may appear)" : "Family-safe mode — explicit content filtered"}>
          <span className={`text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded border ${adult ? "border-[#E50914] text-[#E50914]" : "border-emerald-500/40 text-emerald-400"}`}>{adult ? "18+" : "FAMILY"}</span>
          <span className="relative inline-block">
            <input type="checkbox" checked={adult} onChange={(e) => setAdult(e.target.checked)} data-testid="adult-toggle" className="sr-only" />
            <span className={`block w-9 h-5 rounded-full transition-colors ${adult ? "bg-[#E50914]" : "bg-white/15"}`}>
              <span className={`block w-3.5 h-3.5 rounded-full bg-white transform transition-transform mt-[3px] ${adult ? "translate-x-[19px]" : "translate-x-[3px]"}`} />
            </span>
          </span>
        </label>

        {/* Desktop user / sign-in */}
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <NotificationBell />
              <Link to="/profile" className="flex items-center gap-2" data-testid="nav-profile">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover border border-white/15" />
                ) : (
                  <User className="w-5 h-5" />
                )}
                <GoldBadge size="sm" />
              </Link>
              <button onClick={logout} className="text-neutral-500 hover:text-white" data-testid="nav-logout" title="Log out">
                <LogOut className="w-4 h-4" />
              </button>
              {isAdmin && (
                <Link to="/admin" title="Admin Panel" data-testid="nav-admin-link" className="text-amber-400/70 hover:text-amber-300 transition">
                  <ShieldCheck className="w-4 h-4" />
                </Link>
              )}
            </>
          ) : (
            <GoogleSignInButton onClick={login} testid="nav-login-btn" size="compact" />
          )}
        </div>

        {/* Mobile burger button */}
        <div className="md:hidden ml-auto flex items-center gap-1">
          {user && <NotificationBell compact />}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="nav-burger"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            className="p-2 -mr-2 text-white hover:text-[#E50914] transition-colors"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 top-16 bg-black/70 backdrop-blur-sm z-40"
            onClick={() => setMobileOpen(false)}
            data-testid="nav-mobile-backdrop"
          />
          <div
            className="md:hidden absolute left-0 right-0 top-16 z-50 bg-[#0A0A0A] border-b border-white/10 fm-mobile-drawer"
            data-testid="nav-mobile-drawer"
          >
            <div className="px-4 py-5 space-y-1">
              {/* Mobile search — v2.66 with autocomplete */}
              <div className="mb-4 sm:hidden">
                <SearchAutocomplete
                  placeholder="Search movies, series, actors…"
                  testid="nav-mobile-search-input"
                  compact
                  onSelect={() => setMobileOpen(false)}
                />
              </div>

              <MobileLink to="/" icon={<Home className="w-5 h-5" />} testid="mobile-nav-home">Home</MobileLink>
              <MobileLink to="/discover" icon={<Compass className="w-5 h-5" />} testid="mobile-nav-discover">Discover</MobileLink>
              <MobileLink to="/creators" icon={<Youtube className="w-5 h-5 text-[#FF0000]" />} testid="mobile-nav-creators">Creators</MobileLink>
              <MobileLink to="/concierge" icon={<Sparkles className="w-5 h-5 text-[#007AFF]" />} testid="mobile-nav-concierge">Ask Filmi</MobileLink>
              <MobileLink to="/watchlist" icon={<Bookmark className="w-5 h-5" />} testid="mobile-nav-watchlist">Watchlist</MobileLink>
              <MobileLink to="/social" icon={<Users className="w-5 h-5 text-[#E50914]" />} testid="mobile-nav-social">Filmistani Social</MobileLink>
              <MobileLink to="/support" icon={<Heart className="w-5 h-5 text-[#E50914]" />} testid="mobile-nav-support">Support Us</MobileLink>

              {/* Family-safe toggle */}
              <div className="pt-4 mt-3 border-t border-white/10">
                <label className="flex items-center justify-between gap-3 px-3 py-3 cursor-pointer select-none rounded-md hover:bg-white/[0.03]" data-testid="mobile-adult-toggle-wrap">
                  <div className="flex items-center gap-3">
                    <span className={`text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded border ${adult ? "border-[#E50914] text-[#E50914]" : "border-emerald-500/40 text-emerald-400"}`}>
                      {adult ? "18+" : "FAMILY"}
                    </span>
                    <span className="text-sm text-neutral-300">
                      {adult ? "Mature content allowed" : "Family-safe mode"}
                    </span>
                  </div>
                  <span className="relative inline-block">
                    <input type="checkbox" checked={adult} onChange={(e) => setAdult(e.target.checked)} data-testid="mobile-adult-toggle" className="sr-only" />
                    <span className={`block w-10 h-5 rounded-full transition-colors ${adult ? "bg-[#E50914]" : "bg-white/15"}`}>
                      <span className={`block w-3.5 h-3.5 rounded-full bg-white transform transition-transform mt-[3px] ${adult ? "translate-x-[22px]" : "translate-x-[3px]"}`} />
                    </span>
                  </span>
                </label>
              </div>

              {/* Auth */}
              <div className="pt-3 mt-3 border-t border-white/10">
                {user ? (
                  <>
                    <Link to="/profile" data-testid="mobile-nav-profile"
                          className="flex items-center gap-3 px-3 py-3 rounded-md hover:bg-white/[0.04]">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full object-cover border border-white/15" />
                      ) : (
                        <User className="w-5 h-5 text-neutral-400" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white truncate flex items-center gap-2">
                          <span className="truncate">{user.name || "Profile"}</span>
                          <GoldBadge size="md" />
                        </div>
                        <div className="text-xs text-neutral-500 truncate">{user.email}</div>
                      </div>
                    </Link>
                    <button
                      onClick={() => { setMobileOpen(false); logout(); }}
                      data-testid="mobile-nav-logout"
                      className="w-full flex items-center gap-3 px-3 py-3 rounded-md hover:bg-white/[0.04] text-neutral-300"
                    >
                      <LogOut className="w-5 h-5" /> <span className="text-sm">Log out</span>
                    </button>
                  </>
                ) : (
                  <GoogleSignInButton onClick={login} testid="mobile-nav-login-btn" fullWidth />
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </header>
  );
}

function MobileLink({ to, icon, children, testid }) {
  const loc = useLocation();
  const active = loc.pathname === to;
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`flex items-center gap-3 px-3 py-3 rounded-md transition-colors ${
        active ? "bg-white/[0.06] text-white" : "text-neutral-300 hover:bg-white/[0.03] hover:text-white"
      }`}
    >
      <span className="text-neutral-400">{icon}</span>
      <span className="text-base font-medium">{children}</span>
    </Link>
  );
}
